import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  ArrowLeft,
  Bell,
  Mail,
  Star,
} from 'lucide-react';

import { AuthGuard } from '../../app/AuthGuard';
import type { ScreenId } from '../../app/screenRoutes';
import {
  carrierCabinetApi,
  type BillingSummaryDto,
  type CarrierProfileDto,
  type NotificationDto,
  type OfferDto,
  type TransportDto,
  type TransportRequestDto,
} from '../../features/carrierCabinet/api';
import { formatNotification } from '../../features/chat/messageUtils';
import { isCompletedTransport } from '../../features/carrierCabinet/transportStatus';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { Button, CabinetEmptyState, CabinetLoadState, DateInput, ProfileLink, NotificationMatrixCard, RequestCardUnified, UnifiedNotificationList } from '../../ui';
import { useChatStatus } from '../../state/chatStatus';
import { useLanguage } from '../../i18n/LanguageProvider';
import {
  CarrierCabinetLayout,
  navigateCarrier,
  type CarrierCabinetScreenId,
} from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import {
  budgetLabel,
  carrierBoxLabel,
  dateLabel,
  dateRange,
  documentsStatusLabel,
  hashNumber,
  money,
  PageHeading,
  petLabel,
  petDetails,
  PetIcon,
  relativeTime,
} from './carrierCabinetShared';
import './carrier-cabinet.css';
import './carrier-search-responsive.css';
import './carrier-dashboard-responsive.css';

export type ImplementedCarrierCabinetScreenId = Extract<
  ScreenId,
  'carrier-dashboard' | 'carrier-search' | 'carrier-request-detail' | 'carrier-notifications'
>;

const FIGMA_NODES: Record<ImplementedCarrierCabinetScreenId, Partial<Record<'1920' | '1440' | '768' | '375', string>>> = {
  'carrier-dashboard': { '1920': '2670:36255', '1440': '2670:37436', '768': '2627:17345' },
  'carrier-search': { '1920': '2670:38358', '1440': '2671:37560' },
  'carrier-request-detail': { '1920': '2607:33802', '1440': '2607:34377' },
  'carrier-notifications': { '1920': '2606:32581', '1440': '2606:32868' },
};

const TABLET_DASHBOARD_NODES = {
  root: '2627:17445',
  heading: '2627:17446',
  stats: '2627:17449',
  transport: '2627:17459',
  requests: '2627:17472',
  balance: '2627:17488',
} as const;

function firstName(displayName: string) {
  return displayName.trim().split(/\s+/)[0] || '';
}

type RequestCardVariant = 'compact' | 'search-preferred' | 'search-urgent' | 'search-default';

function requestPrice(request: TransportRequestDto) {
  if (
    request.budget_min_minor != null
    && request.budget_max_minor != null
    && request.budget_min_minor === request.budget_max_minor
  ) {
    return money(request.budget_min_minor, request.currency);
  }
  if (
    request.budget_min_minor != null
    && request.budget_max_minor != null
    && request.budget_min_minor !== request.budget_max_minor
  ) {
    return `от ${money(request.budget_min_minor, request.currency)}`;
  }
  return budgetLabel(request);
}

function searchDateRange(request: TransportRequestDto) {
  const from = request.pickup_not_before ? new Date(`${request.pickup_not_before}T00:00:00`) : null;
  const to = request.delivery_not_after ? new Date(`${request.delivery_not_after}T00:00:00`) : null;
  if (!from && !to) return '';
  if (!from || !to) return dateRange(request);
  if (from.getTime() === to.getTime()) {
    return dateRange(request).split(' – ')[0];
  }
  const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
  ];
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()}–${to.getDate()} ${months[to.getMonth()]}`;
  }
  return dateRange(request).replaceAll(' – ', '–');
}

function searchBudgetLabel(request: TransportRequestDto) {
  const low = request.budget_min_minor;
  const high = request.budget_max_minor;
  if (low == null && high == null) return 'Предложат';
  if (low != null && high != null && low === high) return money(low, request.currency);
  if (low != null && high != null && low !== high) {
    const lowLabel = new Intl.NumberFormat('ru-RU').format(low / 100);
    return `${lowLabel} – ${money(high, request.currency)}`;
  }
  return money(low ?? high, request.currency);
}

function requestBudgetDetailLabel(request: TransportRequestDto, language: 'ru' | 'en') {
  if (request.budget_min_minor == null && request.budget_max_minor == null) {
    return language === 'en' ? 'Awaiting a carrier price' : 'Ожидает цену от перевозчика';
  }
  return budgetLabel(request);
}

function requestUrgencyLabel(value: string, language: 'ru' | 'en') {
  if (!value) return '';
  if (value === 'urgent') return language === 'en' ? 'Urgent' : 'Срочная';
  if (value === 'normal') return language === 'en' ? 'Standard' : 'Обычная';
  return value;
}

function requestFeedingLabel(value: string, language: 'ru' | 'en') {
  if (!value) return '';
  if (value === 'owner_food') return language === 'en' ? 'Food provided' : 'Корм с собой';
  if (value === 'carrier_food') return language === 'en' ? 'Carrier provides food' : 'Кормит перевозчик';
  return value;
}

function requestContactLabel(name: string | null, phone: string | null) {
  const clean = (value: string | null) => value?.trim().replace(/^[·•⋅]+\s*|\s*[·•⋅]+$/g, '') || '';
  return [clean(name), clean(phone)].filter(Boolean).join(' ');
}

function requestDetailDateRange(request: TransportRequestDto, language: 'ru' | 'en') {
  if (language === 'ru') return dateRange(request);
  const format = (value: string | null | undefined) => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    }).format(date);
  };
  const from = format(request.pickup_not_before);
  const to = format(request.delivery_not_after);
  return from && to ? `${from} – ${to}` : from || to;
}

function requestDetailPetDetails(request: TransportRequestDto, language: 'ru' | 'en') {
  const petTypes = language === 'en'
    ? { dog: 'Dog', cat: 'Cat', rodent: 'Rodent', bird: 'Bird', farm: 'Farm animal', other: 'Other animal' }
    : { dog: 'Собака', cat: 'Кошка', rodent: 'Грызун', bird: 'Птица', farm: 'Сельскохозяйственное животное', other: 'Другое животное' };

  return request.pets.map((pet) => [
    petTypes[pet.pet_type],
    language === 'en' && pet.breed === 'Метис' ? 'Mixed breed' : pet.breed || pet.custom_type,
    pet.name ? `${language === 'en' ? 'pet name' : 'кличка'}: ${pet.name}` : '',
    pet.weight_kg ? `${Number(pet.weight_kg)} ${language === 'en' ? 'kg' : 'кг'}` : '',
    pet.count > 1 ? `${pet.count} ${language === 'en' ? 'pets' : 'питомца'}` : '',
  ].filter(Boolean).join(' · ')).join('; ');
}

function openPetPhotosGallery(photos: TransportRequestDto['photos'], initialIndex: number) {
  import('photoswipe').then(async ({ default: PhotoSwipe }) => {
    await import('photoswipe/style.css');
    const items = photos.map((photo, idx) => ({
      src: photo.file.url,
      alt: photo.file.original_filename || `Фото питомца ${idx + 1}`,
    }));

    const lightbox = new PhotoSwipe({
      dataSource: items,
      index: initialIndex,
      bgOpacity: 0.92,
      showAnimationDuration: 0,
      hideAnimationDuration: 0,
      wheelToZoom: true,
      closeOnVerticalDrag: true,
      padding: { top: 40, bottom: 40, left: 30, right: 30 },
      imageClickAction: 'zoom',
      returnFocus: false,
    });

    lightbox.on('gettingData', (event) => {
      const data = event.data;
      if (!data.width || !data.height) {
        const img = new Image();
        img.src = data.src as string;
        if (img.naturalWidth && img.naturalHeight) {
          data.width = img.naturalWidth;
          data.height = img.naturalHeight;
        } else {
          img.onload = () => {
            data.width = img.naturalWidth;
            data.height = img.naturalHeight;
            lightbox.refreshSlideContent(event.index);
          };
          data.width = 1200;
          data.height = 900;
        }
      }
    });

    lightbox.init();
  });
}

function CarrierDetailField({ label, value }: { label: string; value: ReactNode }) {
  if (value === '' || value == null) return null;
  return <div className="carrier-detail-field"><span>{label}</span><strong>{value}</strong></div>;
}

function countLabel(value: number, one: string, few: string, many: string) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function isOnPreferredRoute(request: TransportRequestDto, routes: CarrierProfileDto['preferred_routes']) {
  const from = request.from_city.trim().toLowerCase();
  const to = request.to_city.trim().toLowerCase();
  return routes.some((route) => route.from_city.trim().toLowerCase() === from
    && route.to_city.trim().toLowerCase() === to);
}

function transportDayLabel(transport: TransportDto) {
  if (!transport.departure_date || !transport.delivery_date) return '';
  const start = new Date(`${transport.departure_date}T00:00:00`).getTime();
  const end = new Date(`${transport.delivery_date}T00:00:00`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '';
  const day = Math.min(Math.floor((Date.now() - start) / 86400000) + 1, Math.floor((end - start) / 86400000) + 1);
  const total = Math.floor((end - start) / 86400000) + 1;
  return `день ${Math.max(1, day)}/${total}`;
}

function TabletRequestRow({ request }: { request: TransportRequestDto }) {
  const offers = Number(request.offer_count || 0);
  return (
    <button
      className="carrier-dashboard-tablet-request"
      type="button"
      onClick={() => navigateCarrier('carrier-request-detail', { id: request.id })}
    >
      <span className="carrier-dashboard-tablet-request__top">
        <strong>{request.from_city} → {request.to_city}{request.public_code ? ` · #${request.public_code.replace(/^#/, '')}` : ''}</strong>
        <time dateTime={request.published_at ?? undefined}>{relativeTime(request.published_at)}</time>
      </span>
      <span className="carrier-dashboard-tablet-request__details">
        {[petDetails(request), dateRange(request), budgetLabel(request), `${offers} ${countLabel(offers, 'отклик', 'отклика', 'откликов')}`].filter(Boolean).join(' · ')}
      </span>
    </button>
  );
}

function RequestCard({
  request,
  variant,
  actionLabel = 'Откликнуться',
  onAction,
}: {
  request: TransportRequestDto;
  variant: RequestCardVariant;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const pet = request.pets[0];
  const compact = variant === 'compact';
  const preferred = variant === 'search-preferred';
  const urgent = variant === 'search-urgent';
  const offers = Number(request.offer_count || 0);

  return (
    <RequestCardUnified
      fromCity={request.from_city}
      toCity={request.to_city}
      onRouteClick={!compact ? () => navigateCarrier('carrier-request-detail', { id: request.id }) : undefined}
      requestCode={request.public_code}
      preferred={preferred}
      urgent={urgent}
      petType={pet?.pet_type ?? 'other'}
      petLabel={petLabel(request)}
      petWeight={pet?.weight_kg ? `${Number(pet.weight_kg)} кг` : undefined}
      dateRange={urgent ? '16 июня, срочно' : searchDateRange(request)}
      budget={compact ? searchBudgetLabel(request) : undefined}
      price={!compact ? requestPrice(request) : undefined}
      compact={compact}
      timeAgo={compact ? relativeTime(request.published_at) : undefined}
      actionButton={
        !compact ? (
          <button
            className={`carrier-request-card__cta ${preferred ? 'carrier-green-button' : 'carrier-outline-button'}`}
            type="button"
            onClick={onAction ?? (() => navigateCarrier('carrier-request-detail', { id: request.id }))}
          >
            {actionLabel}
          </button>
        ) : undefined
      }
      onClick={
        compact
          ? onAction ?? (() => navigateCarrier('carrier-request-detail', { id: request.id }))
          : undefined
      }
    />
  );
}

function DashboardView() {
  const breakpoint = useFigmaBreakpoint();
  const { language } = useLanguage();
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [feed, setFeed] = useState<TransportRequestDto[]>([]);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [activeRequest, setActiveRequest] = useState<TransportRequestDto | null>(null);
  const [billing, setBilling] = useState<BillingSummaryDto | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    Promise.all([
      carrierCabinetApi.profile(),
      carrierCabinetApi.feed(),
      carrierCabinetApi.transports(),
      carrierCabinetApi.billing(),
    ]).then(async ([nextProfile, nextFeed, nextTransports, nextBilling]) => {
      if (!active) return;
      setProfile(nextProfile);
      setFeed(nextFeed);
      setTransports(nextTransports);
      setBilling(nextBilling);
      const nextActiveTransport = nextTransports.find((item) => !['delivered', 'closed'].includes(item.status));
      if (nextActiveTransport) {
        const nextActiveRequest = await carrierCabinetApi.request(nextActiveTransport.request_id);
        if (active) setActiveRequest(nextActiveRequest);
      }
      if (!active) return;
      setState('ready');
    }).catch(() => {
      if (active) setState('error');
    });
    return () => { active = false; };
  }, []);

  const activeTransport = transports.find((item) => ['planned', 'in_progress'].includes(item.status));
  const available = billing?.balance.available_minor ?? 0;
  const openInvoice = billing?.open_invoices[0];
  const openInvoiceText = openInvoice
    ? language === 'en'
      ? `Open invoice #${openInvoice.public_code} for ${money(openInvoice.amount_minor, openInvoice.currency)} is still unpaid${openInvoice.is_blocking ? ', so offers on new requests are blocked.' : '.'}`
      : `Открытый счёт #${openInvoice.public_code} на ${money(openInvoice.amount_minor, openInvoice.currency)} — пока не оплачен${openInvoice.is_blocking ? ', отклики на новые заявки заблокированы.' : '.'}`
    : '';
  const invoicePaymentHint = language === 'en'
    ? 'Pay it → offers will be unblocked automatically.'
    : 'Оплатите → разблокируется автоматически.';
  const latestCheckpoint = activeTransport?.checkpoints.at(-1);
  const routeFeed = feed.filter((request) => isOnPreferredRoute(request, profile?.preferred_routes ?? []));
  const dashboardSubtitle = profile
    ? `На ваших маршрутах — ${routeFeed.length} ${countLabel(routeFeed.length, 'открытая заявка', 'открытые заявки', 'открытых заявок')}.${activeTransport ? ' Плюс одна перевозка уже в пути.' : ''}`
    : '';

  if (breakpoint === '768') {
    return (
      <div className="carrier-dashboard carrier-dashboard--tablet" data-state={state} data-figma-node={TABLET_DASHBOARD_NODES.root}>
        <PageHeading
          className="carrier-dashboard-tablet-heading"
          figmaNode={TABLET_DASHBOARD_NODES.heading}
          title={profile ? `Здравствуйте, ${firstName(profile.display_name)}!` : ''}
          subtitle={dashboardSubtitle}
        />

        <section className="carrier-stats" aria-label="Статистика перевозчика" data-figma-node={TABLET_DASHBOARD_NODES.stats}>
          <div><strong>★ {profile?.review_rating ?? ''}</strong><span>Рейтинг</span></div>
          <div><strong>{profile?.completed_transports_count ?? ''}</strong><span>Перевозок выполнено</span></div>
          <div><strong>{profile ? `${profile.photo_rating_percent}%` : ''}</strong><span>Рейтинг фото</span></div>
        </section>

        <section className="carrier-dashboard-tablet-card carrier-dashboard-tablet-card--transport" data-figma-node={TABLET_DASHBOARD_NODES.transport}>
          <header>
            <h2>Активная перевозка</h2>
            {activeTransport ? <button type="button" onClick={() => navigateCarrier('carrier-active-transport', { id: activeTransport.id })}>Подробнее →</button> : null}
          </header>
          {activeTransport ? (
            <>
              <div className="carrier-dashboard-tablet-transport-copy">
                <strong>{activeTransport.from_city} → {activeTransport.to_city}{activeRequest ? ` · ${petLabel(activeRequest)}` : ''}</strong>
                <span>{[
                  activeTransport.customer_name ? `Заказчик: ${activeTransport.customer_name}` : '',
                  transportDayLabel(activeTransport),
                  latestCheckpoint?.title ? `сейчас: ${latestCheckpoint.title}` : '',
                ].filter(Boolean).join(' · ')}</span>
              </div>
              <div className="carrier-dashboard-tablet-price"><strong>{money(activeTransport.net_price_minor, activeTransport.currency)}</strong><span>чистыми</span></div>
              <p className="carrier-dashboard-tablet-photo-rating">Рейтинг фото: {profile?.photo_rating_percent ?? 0}%</p>
              <div className="carrier-dashboard-tablet-alert">⚠ Загрузите фото текущего этапа — это улучшит рейтинг фото.</div>
            </>
          ) : (
            <p className="carrier-dashboard-empty">
              Сейчас у вас нет активных перевозок. Откликайтесь на заявки, чтобы взять заказ.
            </p>
          )}
        </section>

        <section className="carrier-dashboard-tablet-card carrier-dashboard-tablet-card--requests" data-figma-node={TABLET_DASHBOARD_NODES.requests}>
          <header><h2>Новые заявки на маршрутах</h2><button type="button" onClick={() => navigateCarrier('carrier-search')}>Все →</button></header>
          <div className="carrier-dashboard-tablet-requests">
            {routeFeed.slice(0, 2).map((request) => <TabletRequestRow key={request.id} request={request} />)}
            {routeFeed.length === 0 ? <p>Пока нет открытых заявок на ваших маршрутах.</p> : null}
          </div>
        </section>

        <section className="carrier-dashboard-tablet-card carrier-dashboard-tablet-card--balance" data-figma-node={TABLET_DASHBOARD_NODES.balance}>
          <header>
            <div><span>Внутренний баланс LOVO</span><strong>{billing ? money(available, billing.balance.currency) : ''}</strong></div>
            <button type="button" onClick={() => navigateCarrier('carrier-earnings')}>К счетам →</button>
          </header>
          {openInvoice ? (
            <p className="carrier-balance-card__alert" data-i18n-skip>
              <span>{openInvoiceText}</span>
              <small>{invoicePaymentHint}</small>
            </p>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div className="carrier-dashboard" data-state={state}>
      <PageHeading
        title={profile ? `Здравствуйте, ${firstName(profile.display_name)}!` : ''}
        subtitle={dashboardSubtitle}
      />

      <section className="carrier-stats" aria-label="Статистика перевозчика">
        <div><strong><Star aria-hidden="true" size={24} fill="currentColor" />{profile ? Number(profile.review_rating) : ''}</strong><span>Рейтинг</span></div>
        <div><strong>{profile?.completed_transports_count ?? ''}</strong><span>Перевозок выполнено</span></div>
        <div><strong>{profile ? `${profile.photo_rating_percent}%` : ''}</strong><span>Рейтинг фото</span></div>
      </section>

      <section className="carrier-dashboard-card carrier-dashboard-card--transport">
        <header><h2>Активная перевозка</h2>{activeTransport ? <button type="button" onClick={() => navigateCarrier('carrier-active-transport', { id: activeTransport.id })}>Подробнее <img src="/figma/assets/carrier-card-route-arrow.svg" alt="" aria-hidden="true" /></button> : null}</header>
        {activeTransport ? (
          <>
            <div className="carrier-dashboard-transport">
              <div className="carrier-dashboard-transport__copy">
                <strong>{activeTransport.from_city} → {activeTransport.to_city}{activeRequest ? ` · ${activeRequest.pets[0]?.name || petLabel(activeRequest)} (${petLabel(activeRequest)})` : ''}</strong>
                <span>{[
                  activeTransport.customer_name ? `Заказчик: ${activeTransport.customer_name}` : '',
                  transportDayLabel(activeTransport),
                  latestCheckpoint?.title ? `сейчас в ${latestCheckpoint.title}` : '',
                ].filter(Boolean).join(' · ')}</span>
              </div>
              <div className="carrier-dashboard-transport__price">
                <div><strong>{money(activeTransport.net_price_minor, activeTransport.currency)}</strong><span>чистыми</span></div>
                <em>Рейтинг фото: {profile?.photo_rating_percent ?? 0}%</em>
              </div>
            </div>
            <p className="carrier-dashboard-transport__alert">Загрузите фото текущего этапа — это улучшит рейтинг фото и повысит шанс выбора в следующих заявках</p>
          </>
        ) : (
          <p className="carrier-dashboard-empty">
            Сейчас у вас нет активных перевозок. Откликайтесь на заявки, чтобы взять заказ.
          </p>
        )}
      </section>

      <section className="carrier-dashboard-card carrier-dashboard-card--requests">
        <header><h2>Новые заявки на ваших маршрутах</h2><button type="button" onClick={() => navigateCarrier('carrier-search')}>Все заявки <img src="/figma/assets/carrier-card-route-arrow.svg" alt="" aria-hidden="true" /></button></header>
        <div className="carrier-dashboard-requests">
          {routeFeed.slice(0, 2).map((request) => <RequestCard key={request.id} request={request} variant="compact" />)}
          {routeFeed.length === 0 ? <p>Пока нет открытых заявок на ваших маршрутах.</p> : null}
        </div>
      </section>

      <section className="carrier-balance-card">
        <header>
          <div>
            <span>Внутренний баланс LOVO</span>
            <strong>{billing ? money(available, billing.balance.currency) : ''}</strong>
          </div>
          <button type="button" onClick={() => navigateCarrier('carrier-earnings')}>
            К счетам и балансу
            <svg aria-hidden="true" fill="none" height="15" preserveAspectRatio="none" viewBox="0 0 15 15" width="15" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 14.0833L14.0833 0.75M14.0833 14.0833V0.75H0.75" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
            </svg>
          </button>
        </header>
        {openInvoice ? (
          <p className="carrier-balance-card__alert" data-i18n-skip>
            <span>{openInvoiceText}</span>
            <small>{invoicePaymentHint}</small>
          </p>
        ) : (
          <p className="carrier-balance-card__alert carrier-balance-card__alert--quiet">
            Нет открытых счетов. Баланс доступен для расчётов по завершённым перевозкам.
          </p>
        )}
      </section>
    </div>
  );
}

type AnimalFilter = 'dog' | 'cat' | 'rodent' | 'bird' | 'farm' | 'other';

function defaultSearchDate(offsetDays: number) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function SearchView() {
  const { language } = useLanguage();
  const [savedOnly, setSavedOnly] = useState(false);
  const [feed, setFeed] = useState<TransportRequestDto[]>([]);
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  type SortOption = 'newest' | 'oldest' | 'price_desc' | 'price_asc';
  const SORT_LABELS: Record<SortOption, string> = {
    newest: 'Сначала новые',
    oldest: 'Сначала старые',
    price_desc: 'Сначала дороже',
    price_asc: 'Сначала дешевле',
  };

  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [sortOpen, setSortOpen] = useState(false);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [transportRequests, setTransportRequests] = useState<Map<number, TransportRequestDto>>(new Map());
  const [fromCity, setFromCity] = useState('Москва');
  const [toCity, setToCity] = useState('');
  const [dateFrom, setDateFrom] = useState(() => defaultSearchDate(0));
  const [dateTo, setDateTo] = useState(() => defaultSearchDate(5));
  const [animals, setAnimals] = useState<AnimalFilter[]>(['dog', 'cat']);
  const [applied, setApplied] = useState({
    fromCity: '',
    toCity: '',
    dateFrom: '',
    dateTo: '',
    animals: [] as AnimalFilter[],
  });
  const [tab, setTab] = useState<'routes' | 'active' | 'transit'>('active');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    Promise.all([
      carrierCabinetApi.profile(),
      carrierCabinetApi.feed(),
      carrierCabinetApi.transports(),
      carrierCabinetApi.offers().catch(() => []),
    ]).then(async ([nextProfile, items, nextTransports, nextOffers]) => {
      if (!active) return;
      setProfile(nextProfile);
      setFeed(items);
      setTransports(nextTransports);
      setOffers(nextOffers);
      const requestIds = [...new Set(
        nextTransports
          .filter((transport) => transport.carrier_id === nextProfile.id && !isCompletedTransport(transport))
          .map((transport) => transport.request_id),
      )];
      const requestResults = await Promise.all(
        requestIds.map(async (id) => [id, await carrierCabinetApi.request(id)] as const),
      );
      if (!active) return;
      setTransportRequests(new Map(requestResults));
      setState('ready');
    }).catch(() => {
      if (active) setState('error');
    });
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => feed.filter((request) => {
    if (['completed', 'closed'].includes(request.status) || transports.some((transport) => transport.request_id === request.id && isCompletedTransport(transport))) return false;
    const petTypes = request.pets.map((pet) => pet.pet_type);
    const routeMatches = request.from_city.toLowerCase().includes(applied.fromCity.toLowerCase())
      && request.to_city.toLowerCase().includes(applied.toCity.toLowerCase());
    const dateMatches = (!applied.dateFrom || !request.delivery_not_after || request.delivery_not_after >= applied.dateFrom)
      && (!applied.dateTo || !request.pickup_not_before || request.pickup_not_before <= applied.dateTo);
    const animalMatches = !applied.animals.length || applied.animals.some((animal) => petTypes.includes(animal));
    return routeMatches && dateMatches && animalMatches;
  }), [applied, feed, transports]);

  const preferredRequestIds = useMemo(() => {
    const routes = profile?.preferred_routes ?? [];
    return new Set(filtered.filter((request) => isOnPreferredRoute(request, routes)).map((request) => request.id));
  }, [filtered, profile]);

  const urgentRequestIds = useMemo(() => new Set(feed.filter((request) => (
    ['urgent', 'asap'].includes(request.urgency.toLowerCase())
  )).map((request) => request.id)), [feed]);

  const carrierTransports = useMemo(
    () => transports.filter((transport) => transport.carrier_id === profile?.id),
    [profile?.id, transports],
  );
  const transitTransports = useMemo(
    () => carrierTransports.filter((transport) => transport.status === 'in_progress' && !isCompletedTransport(transport)),
    [carrierTransports],
  );
  const transitRequests = useMemo(
    () => transitTransports.map((transport) => transportRequests.get(transport.request_id)).filter((request): request is TransportRequestDto => Boolean(request)),
    [transitTransports, transportRequests],
  );

  const visible = useMemo(() => {
    const candidates = savedOnly ? filtered.filter((request) => request.is_saved) : filtered;
    if (tab === 'routes') return candidates.filter((request) => preferredRequestIds.has(request.id));
    if (tab === 'transit') return transitRequests;
    return candidates;
  }, [filtered, preferredRequestIds, savedOnly, tab, transitRequests]);

  const tabCounts = {
    routes: preferredRequestIds.size,
    active: filtered.length,
    transit: transitTransports.length,
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    setApplied({ fromCity, toCity, dateFrom, dateTo, animals });
  }

  function toggleAnimal(animal: AnimalFilter) {
    setAnimals((current) => current.includes(animal) ? current.filter((item) => item !== animal) : [...current, animal]);
  }

  return (
    <div className="carrier-search" data-state={state}>
      <PageHeading
        title="Поиск заявок"
        subtitle="Заявки на ваших маршрутах и по всей базе LOVO"
        action={<button className="carrier-outline-button" type="button" onClick={() => navigateCarrier('carrier-notifications')}>Настроить уведомления</button>}
      />

      <form className="carrier-search-filters" onSubmit={submit}>
        <div className="carrier-search-filters__fields">
          <label className="carrier-search-field">
            <img src="/figma/assets/carrier-filter-geo.svg" alt="" aria-hidden="true" />
            <span><b>Откуда</b><input value={fromCity} onChange={(event) => setFromCity(event.target.value)} /></span>
          </label>
          <label className="carrier-search-field">
            <img src="/figma/assets/carrier-filter-geo.svg" alt="" aria-hidden="true" />
            <span><b>Куда</b><input value={toCity} placeholder="Любой город" onChange={(event) => setToCity(event.target.value)} /></span>
          </label>
          <label className="carrier-search-field carrier-search-field--date">
            <img src="/figma/assets/carrier-filter-calendar.svg" alt="" aria-hidden="true" />
            <span><b>Выезд от</b><strong>{dateFrom ? dateLabel(dateFrom) : 'Выберите дату'}</strong></span>
            <DateInput value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="carrier-search-field carrier-search-field--date">
            <img src="/figma/assets/carrier-filter-calendar.svg" alt="" aria-hidden="true" />
            <span><b>Выезд до</b><strong>{dateTo ? dateLabel(dateTo) : 'Выберите дату'}</strong></span>
            <DateInput min={dateFrom || undefined} value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <button className="carrier-green-button" type="submit">Применить</button>
        </div>
        <div className="carrier-animal-filters">
          {([
            ['dog', 'Собаки'], ['cat', 'Кошки'], ['rodent', 'Грызуны'],
            ['bird', 'Птицы'], ['farm', 'Сельхоз'], ['other', 'Другое'],
          ] as Array<[AnimalFilter, string]>).map(([value, label]) => (
            <label key={value}>
              <input type="checkbox" checked={animals.includes(value)} onChange={() => toggleAnimal(value)} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </form>

      <div className="carrier-feed-filters">
        <div className="carrier-feed-tabs" role="tablist" aria-label="Фильтр заявок">
          <button className={tab === 'routes' ? 'is-active' : ''} type="button" role="tab" aria-selected={tab === 'routes'} onClick={() => setTab('routes')}>На моих маршрутах <span>{tabCounts.routes}</span></button>
          <button className={tab === 'active' ? 'is-active' : ''} type="button" role="tab" aria-selected={tab === 'active'} onClick={() => setTab('active')}>Активные <span>{tabCounts.active}</span></button>
          <button className={tab === 'transit' ? 'is-active' : ''} type="button" role="tab" aria-selected={tab === 'transit'} onClick={() => setTab('transit')}>В пути <span>{tabCounts.transit}</span></button>
        </div>
        <div className="carrier-sort-wrapper">
          <button
            className="carrier-outline-button carrier-save-search"
            type="button"
            onClick={() => setSortOpen((current) => !current)}
          >
            {SORT_LABELS[sortBy]}
            <img src="/figma/assets/carrier-sort-arrow.svg" alt="" aria-hidden="true" />
          </button>
          {sortOpen ? (
            <div className="carrier-sort-menu">
              {(Object.keys(SORT_LABELS) as SortOption[]).map((option) => (
                <button
                  key={option}
                  className={`carrier-sort-menu__item ${sortBy === option ? 'is-selected' : ''}`}
                  type="button"
                  onClick={() => {
                    setSortBy(option);
                    setSortOpen(false);
                  }}
                >
                  {SORT_LABELS[option]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="carrier-search-note">
        <p className="carrier-route-note">Заявки на ваших маршрутах отмечены — по ним у вас больше шансов на выбор</p>
        {tab === 'active' || tab === 'routes' ? (
          <label className="carrier-saved-filter" data-i18n-skip>
            <input type="checkbox" checked={savedOnly} onChange={(event) => setSavedOnly(event.target.checked)} />
            {language === 'en' ? 'Saved only' : 'Только сохранённые'}
          </label>
        ) : null}
      </div>
      <div className="carrier-request-list">
        <CabinetLoadState state={state} />
        {state === 'ready' && visible.length === 0 ? <CabinetEmptyState kind="search" title={savedOnly && (tab === 'active' || tab === 'routes') ? (language === 'en' ? 'No saved requests match these filters.' : 'Нет сохранённых заявок по выбранным фильтрам.') : undefined} /> : null}
        {visible
          .slice()
          .sort((left, right) => {
            const getPriceValue = (req: TransportRequestDto) => {
              if (req.budget_min_minor != null) return req.budget_min_minor;
              if (req.budget_max_minor != null) return req.budget_max_minor;
              return 0;
            };

            if (sortBy === 'price_desc') {
              return getPriceValue(right) - getPriceValue(left);
            }
            if (sortBy === 'price_asc') {
              return getPriceValue(left) - getPriceValue(right);
            }
            if (sortBy === 'newest') {
              const dateDiff = (new Date(right.published_at ?? 0).getTime()) - (new Date(left.published_at ?? 0).getTime());
              if (dateDiff !== 0) return dateDiff;
            }
            if (sortBy === 'oldest') {
              const dateDiff = (new Date(left.published_at ?? 0).getTime()) - (new Date(right.published_at ?? 0).getTime());
              if (dateDiff !== 0) return dateDiff;
            }

            if (tab === 'routes' || tab === 'active') {
              return Number(preferredRequestIds.has(right.id)) - Number(preferredRequestIds.has(left.id));
            }
            return 0;
          })
          .map((request) => {
            const transport = carrierTransports.find((item) => item.request_id === request.id);
            const transportTab = tab === 'transit';
            const existingOffer = offers.find((offer) => offer.request === request.id);
            return (
              <RequestCard
                key={request.id}
                request={request}
                variant={urgentRequestIds.has(request.id)
                  ? 'search-urgent'
                  : preferredRequestIds.has(request.id)
                    ? 'search-preferred'
                    : 'search-default'}
                actionLabel={transportTab ? 'Открыть' : existingOffer ? 'Отклик отправлен' : 'Откликнуться'}
                onAction={transportTab && transport
                  ? () => navigateCarrier('carrier-active-transport', { id: transport.id })
                  : existingOffer
                    ? () => navigateCarrier('carrier-my-offers')
                    : undefined}
              />
            );
          })}
      </div>
    </div>
  );
}

function RequestDetailView() {
  const { language } = useLanguage();
  const requestId = hashNumber('id');
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [existingOffer, setExistingOffer] = useState<OfferDto | null>(null);
  const [offerCount, setOfferCount] = useState(0);
  const [question, setQuestion] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [questionSending, setQuestionSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const load = async () => {
      const id = requestId ?? (await carrierCabinetApi.feed())[0]?.id;
      if (!id) {
        if (active) setState('error');
        return;
      }
      const [item, offers] = await Promise.all([
        carrierCabinetApi.request(id),
        carrierCabinetApi.offers().catch(() => []),
      ]);
      if (!active) return;
      setRequest(item);
      setExistingOffer(offers.find((offer) => offer.request === id) ?? null);
      setOfferCount(Number(item.offer_count || 0));
      setState('ready');
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [requestId]);

  async function askPublicQuestion() {
    if (!request || !question.trim()) return;
    setQuestionSending(true);
    setQuestionError('');
    try {
      await carrierCabinetApi.askQuestion(request.id, question.trim());
      setRequest(await carrierCabinetApi.request(request.id));
      setQuestion('');
    } catch (error) {
      setQuestionError(error instanceof Error ? error.message : 'Не удалось отправить вопрос.');
    } finally {
      setQuestionSending(false);
    }
  }

  async function toggleSaved() {
    if (!request || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const result = await carrierCabinetApi.setRequestSaved(request.id, !request.is_saved);
      setRequest((current) => current?.id === request.id ? { ...current, is_saved: result.is_saved } : current);
    } catch {
      setSaveError(language === 'en' ? 'Could not update saved requests. Please try again.' : 'Не удалось изменить сохранённые заявки. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  }

  function openOffer() {
    if (!request) return;
    navigateCarrier(existingOffer ? 'carrier-my-offers' : 'carrier-offer', existingOffer ? undefined : { id: request.id });
  }

  const offerLabel = existingOffer
    ? language === 'en' ? 'Offer sent' : 'Отклик отправлен'
    : language === 'en' ? 'Send offer' : 'Отправить отклик';

  return (
    <div className="carrier-request-detail" data-state={state}>
      <button className="carrier-back-button" type="button" data-i18n-skip onClick={() => navigateCarrier('carrier-search')}>
        <ArrowLeft size={16} aria-hidden="true" />
        {language === 'en' ? 'Back to requests' : 'Назад к списку заявок'}
      </button>
      {state === 'loading' ? (
        <section className="carrier-request-detail__status" aria-live="polite">Загружаем заявку...</section>
      ) : null}
      {state === 'error' ? (
        <section className="carrier-request-detail__status carrier-request-detail__status--error" role="alert">
          <strong>Не удалось открыть заявку</strong>
          <span>Возможно, она уже закрыта или больше недоступна.</span>
          <button className="carrier-outline-button" type="button" onClick={() => navigateCarrier('carrier-search')}>Вернуться к списку</button>
        </section>
      ) : null}
      {request ? (
        <>
          <PageHeading
            className="carrier-request-detail__heading"
            title={`${request.from_city} → ${request.to_city}`}
            subtitle={<span>#{request.public_code}</span>}
            action={(
              <div className="carrier-request-detail__top-actions" data-i18n-skip>
                <button
                  className="carrier-outline-button"
                  type="button"
                  aria-pressed={Boolean(request.is_saved)}
                  disabled={saving || (!request.is_saved && request.status !== 'published')}
                  onClick={() => void toggleSaved()}
                >
                  <Star size={16} fill={request.is_saved ? 'currentColor' : 'none'} aria-hidden="true" />
                  {saving ? language === 'en' ? 'Saving…' : 'Сохраняем…'
                    : request.is_saved ? language === 'en' ? 'Saved' : 'В сохранённых'
                      : language === 'en' ? 'Save request' : 'В сохранённые'}
                </button>
                <button className="carrier-green-button" type="button" disabled={!existingOffer && request.status !== 'published'} onClick={openOffer}>{offerLabel}</button>
              </div>
            )}
          />
          {saveError ? <p className="carrier-request-detail__save-error" role="alert" data-i18n-skip>{saveError}</p> : null}

          <section className="carrier-customer-card">
            <h2>Заказчик</h2>
            <div>
              <span className="carrier-customer-card__avatar"><ProfileLink profileId={request.customer_id} role="customer">{request.customer_name?.slice(0, 1).toUpperCase()}</ProfileLink></span>
              <div><strong><ProfileLink profileId={request.customer_id} role="customer">{request.customer_name}</ProfileLink></strong><span>★ {request.customer_rating} · {request.customer_completed_requests_count} перевозок</span></div>
              <a
                className="carrier-outline-button"
                href={`#profile-external?customer=${request.customer_id}`}
              >
                Профиль
              </a>
            </div>
          </section>

          <section className="carrier-detail-card">
            <h2>Кого везти</h2>
            <div className="carrier-detail-grid">
              <CarrierDetailField label="Питомец" value={requestDetailPetDetails(request, language)} />
              <CarrierDetailField label="Откуда" value={request.from_city} />
              <CarrierDetailField label="Куда" value={request.to_city} />
              <CarrierDetailField label="Сроки" value={requestDetailDateRange(request, language)} />
              <CarrierDetailField label={language === 'en' ? 'Urgency' : 'Срочность'} value={requestUrgencyLabel(request.urgency, language)} />
              <CarrierDetailField label="Переноска" value={carrierBoxLabel(request.carrier_box)} />
              <CarrierDetailField label="Документы" value={documentsStatusLabel(request.documents_status)} />
              <CarrierDetailField label="Особенности" value={request.pet_notes} />
              <CarrierDetailField label={language === 'en' ? 'Feeding' : 'Кормление'} value={requestFeedingLabel(request.feeding_notes, language)} />
              <CarrierDetailField label="Бюджет" value={requestBudgetDetailLabel(request, language)} />
              <CarrierDetailField
                label={language === 'en' ? 'Pickup contact' : 'Контакт при заборе'}
                value={requestContactLabel(request.pickup_contact_name, request.pickup_contact_phone)}
              />
              <CarrierDetailField
                label={language === 'en' ? 'Delivery contact' : 'Контакт при доставке'}
                value={requestContactLabel(request.delivery_contact_name, request.delivery_contact_phone)}
              />
              <CarrierDetailField label={language === 'en' ? 'Pickup notes' : 'Ориентир при заборе'} value={request.pickup_contact_notes} />
              <CarrierDetailField label={language === 'en' ? 'Delivery notes' : 'Ориентир при доставке'} value={request.delivery_contact_notes} />
            </div>
            {request.photos.length ? (
              <div className="carrier-pet-photos">
                {request.photos.slice(0, 4).map((photo, index) => (
                  <button
                    key={photo.id}
                    type="button"
                    className="carrier-pet-photo-btn"
                    aria-label={`Открыть фото питомца ${index + 1}`}
                    onClick={() => openPetPhotosGallery(request.photos, index)}
                  >
                    <img src={photo.file.url} alt={`Фото питомца ${index + 1}`} />
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="carrier-public-questions">
            <h2>{language === 'en' ? 'Public questions' : 'Публичные вопросы'}{request.questions.length ? ` · ${request.questions.length}` : ''}</h2>
            <p>{language === 'en'
              ? 'Ask a public question before sending an offer. All carriers will see the customer’s answer. Contact details and personal information are not allowed here.'
              : 'Задайте общий вопрос до отклика. Ответ заказчика увидят все перевозчики. Контакты и личные данные здесь запрещены.'}</p>
            {request.questions.length ? (
              <div className="carrier-public-questions__list">
                {request.questions.map((item) => (
                  <article key={item.id}>
                    <div><strong><ProfileLink profileId={item.carrier} role="carrier">{item.carrier_name}</ProfileLink></strong><span>{item.answer
                      ? language === 'en' ? 'Answered' : 'Есть ответ'
                      : item.moderation_status === 'pending'
                        ? language === 'en' ? 'Under review' : 'На модерации'
                        : language === 'en' ? 'Awaiting answer' : 'Ждёт ответа'}</span></div>
                    <p>{item.question}</p>
                    {item.answer ? <blockquote><b>{language === 'en' ? 'Customer’s answer' : 'Ответ заказчика'}</b>{item.answer}</blockquote> : null}
                  </article>
                ))}
              </div>
            ) : <span className="carrier-public-questions__empty">{language === 'en' ? 'No public questions yet' : 'Публичных вопросов пока нет'}</span>}
            {request.status === 'published' ? (
              <div className="carrier-public-questions__form">
                <input value={question} placeholder={language === 'en' ? 'Ask a public question…' : 'Задать публичный вопрос…'} onChange={(event) => setQuestion(event.target.value)} />
                <button className="carrier-green-button" disabled={questionSending || !question.trim()} type="button" onClick={() => void askPublicQuestion()}>{questionSending
                  ? language === 'en' ? 'Sending…' : 'Отправляем…'
                  : language === 'en' ? 'Ask publicly' : 'Спросить публично'}</button>
              </div>
            ) : null}
            {questionError ? <span className="carrier-public-questions__error" role="alert">{questionError}</span> : null}
          </section>

          <section className="carrier-offer-summary">
            <strong>{language === 'en' ? 'Offers submitted' : 'Уже откликнулись'} · {offerCount}</strong>
            <span>Данные других перевозчиков скрыты до отправки вашего отклика.</span>
          </section>
          <div className="carrier-detail-actions">
            <button className="carrier-outline-button" type="button" onClick={() => navigateCarrier('carrier-search')}>Отмена</button>
            <button
              className="carrier-green-button"
              type="button"
              data-i18n-skip
              disabled={!existingOffer && request.status !== 'published'}
              onClick={openOffer}
            >
              {offerLabel}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function notificationTarget(notification: NotificationDto) {
  const allowed = new Set<CarrierCabinetScreenId>([
    'carrier-dashboard', 'carrier-search', 'carrier-request-detail', 'carrier-offer',
    'carrier-my-offers', 'carrier-chat-list', 'carrier-chat', 'carrier-active-transport',
    'carrier-completed', 'carrier-earnings', 'carrier-review', 'carrier-notifications', 'carrier-dispute',
  ]);
  const requested = notification.target_screen.startsWith('carrier-')
    ? notification.target_screen
    : notification.target_screen === 'chat' ? 'carrier-chat' : 'carrier-dashboard';
  return allowed.has(requested as CarrierCabinetScreenId) ? requested as CarrierCabinetScreenId : 'carrier-dashboard';
}

type CarrierNotificationChannel = 'push' | 'email' | 'sms';
type CarrierNotificationPreference = 'new_offers' | 'chat_messages' | 'public_questions' | 'checkpoints' | 'disputes' | 'news';
type CarrierNotificationPreferences = Record<CarrierNotificationPreference, Record<CarrierNotificationChannel, boolean>>;

const CARRIER_NOTIFICATION_LABELS: Record<CarrierNotificationPreference, string> = {
  new_offers: 'Новые отклики и заявки',
  chat_messages: 'Сообщения в чате',
  public_questions: 'Вопросы по заявкам',
  checkpoints: 'Статусы перевозки',
  disputes: 'Споры и решения',
  news: 'Новости LOVO',
};

const DEFAULT_CARRIER_NOTIFICATION_PREFERENCES: CarrierNotificationPreferences = {
  new_offers: { push: true, email: true, sms: false },
  chat_messages: { push: true, email: true, sms: false },
  public_questions: { push: true, email: false, sms: false },
  checkpoints: { push: true, email: false, sms: true },
  disputes: { push: true, email: true, sms: true },
  news: { push: false, email: false, sms: false },
};

function carrierPreferences(profile?: CarrierProfileDto): CarrierNotificationPreferences {
  return Object.fromEntries(
    (Object.keys(DEFAULT_CARRIER_NOTIFICATION_PREFERENCES) as CarrierNotificationPreference[]).map((key) => [
      key,
      { ...DEFAULT_CARRIER_NOTIFICATION_PREFERENCES[key], ...(profile?.notification_preferences?.[key] ?? {}) },
    ]),
  ) as CarrierNotificationPreferences;
}

function NotificationsView() {
  const chatStatus = useChatStatus();
  const { language } = useLanguage();
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [preferences, setPreferences] = useState<CarrierNotificationPreferences>(DEFAULT_CARRIER_NOTIFICATION_PREFERENCES);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    Promise.all([carrierCabinetApi.notifications(), carrierCabinetApi.profile()]).then(([notifications, profile]) => {
      if (!active) return;
      setItems(notifications);
      setPreferences(carrierPreferences(profile));
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [chatStatus.eventVersion]);

  async function open(notification: NotificationDto, groupedIds?: number[]) {
    const idsToMark = groupedIds && groupedIds.length > 0 ? groupedIds : [notification.id];
    const unreadIds = idsToMark.filter((id) => {
      const target = items.find((n) => n.id === id);
      return !target?.read_at;
    });

    if (unreadIds.length > 0) {
      await Promise.allSettled(unreadIds.map((id) => carrierCabinetApi.markNotificationRead(id)));
      const now = new Date().toISOString();
      setItems((current) => current.map((item) => idsToMark.includes(item.id) ? { ...item, read_at: item.read_at ?? now } : item));
    }
    await chatStatus.refresh();
    const target = notificationTarget(notification);
    const param = target === 'carrier-chat' ? 'chat' : 'id';
    navigateCarrier(target, notification.target_id ? { [param]: notification.target_id } : undefined);
  }

  async function readAll() {
    await carrierCabinetApi.markAllNotificationsRead();
    const readAt = new Date().toISOString();
    setItems((current) => current.map((item) => ({ ...item, read_at: item.read_at ?? readAt })));
    await chatStatus.refresh();
  }

  async function togglePreference(key: CarrierNotificationPreference, channel: CarrierNotificationChannel) {
    if (savingPreferences) return;
    const previous = preferences;
    const next = {
      ...preferences,
      [key]: { ...preferences[key], [channel]: !preferences[key][channel] },
    };
    setPreferences(next);
    setSavingPreferences(true);
    try {
      const profile = await carrierCabinetApi.updateProfile({ notification_preferences: next });
      setPreferences(carrierPreferences(profile));
    } catch {
      setPreferences(previous);
    } finally {
      setSavingPreferences(false);
    }
  }

  return (
    <div className="carrier-notifications" data-state={state}>
      <PageHeading
        title="Уведомления"
        subtitle="Что происходит по вашим заявкам и сделкам"
        action={<div className="carrier-notifications__actions"><Button kind="secondary" size="medium" onClick={() => setSettingsOpen((open) => !open)}>Настроить</Button><Button kind="secondary" size="medium" onClick={() => void readAll()}>Прочитать все</Button></div>}
      />
      {settingsOpen ? (
        <NotificationMatrixCard
          categories={CARRIER_NOTIFICATION_LABELS}
          disabled={savingPreferences}
          preferences={preferences}
          onTogglePreference={(key, channel) => void togglePreference(key as CarrierNotificationPreference, channel)}
        />
      ) : null}
      <UnifiedNotificationList
        role="carrier"
        state={state}
        key={language}
        items={items.map((notification) => {
          const target = notificationTarget(notification);
          const isLink = Boolean(target === 'carrier-chat' || notification.target_id);
          const actionText = notification.notification_type === 'invoice_issued'
            ? (language === 'en' ? 'Open invoice' : 'Открыть счёт')
            : target === 'carrier-chat'
            ? (language === 'en' ? 'Open chat' : 'Открыть чат')
            : isLink
            ? (language === 'en' ? 'Open' : 'Открыть')
            : (language === 'en' ? 'Mark as read' : 'Пометить прочитанным');

          return {
            ...notification,
            isLink,
            actionText,
          };
        })}
        language={language}
        onItemClick={(item, groupedIds) => void open(item, groupedIds)}
      />
    </div>
  );
}

function CarrierCabinetContent({ screen }: { screen: ImplementedCarrierCabinetScreenId }) {
  if (screen === 'carrier-dashboard') return <DashboardView />;
  if (screen === 'carrier-search') return <SearchView />;
  if (screen === 'carrier-request-detail') return <RequestDetailView />;
  return <NotificationsView />;
}

export function CarrierCabinetScreen({ screen }: { screen: ImplementedCarrierCabinetScreenId }) {
  return (
    <AuthGuard allow={['carrier']}>
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen]} screen={screen}>
        <CarrierCabinetContent screen={screen} />
      </CarrierCabinetLayout>
    </AuthGuard>
  );
}
